import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Snackbar,
    Alert
} from '@mui/material';
import i18n from '../../i18';
import { ProfileCircleBulkIcon } from '../../icons-lib';
import SearchBox from '../../search-box/SearchBox';
import { fetchFirstItems } from '../../search-box/fetchFilteredItems';
// Excel parsing library
import * as XLSX from 'xlsx';

const dragStyles = {
    border: '1px dashed #D0D5DD',
    borderRadius: 1,
    padding: '40px 16px',
    textAlign: 'center',
    color: '#98A2B3',
    cursor: 'pointer'
};

export default function BatchUploadDialog({
    open,
    onClose,
    onUpload,
    context,
    userInfo
}) {
    const [step, setStep] = useState(0);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [initialCompanies, setInitialCompanies] = useState([]);
    const [totalCompaniesCount, setTotalCompaniesCount] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [alertText, setAlertText] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('error');

    // Helper to reset local state when dialog closes or user cancels
    const resetState = () => {
        setStep(0);
        setSelectedCompany(null);
        setFile(null);
        setPreviewData([]);
    };

    // Wrap external onClose so we can reset before closing
    const handleDialogClose = () => {
        resetState();
        onClose();
    };

    // load initial list of SME companies for the select so that list appears immediately
    useEffect(() => {
        let companies = context.getState().controllersData?.smeCompanies || [];
        companies = applyRoleFilter(companies);
        setInitialCompanies(companies);
        setTotalCompaniesCount(companies.length);

        // if nothing in context, fetch first 20 from server
        if (companies.length === 0) {
            const serverController = context.getState().server['sme-companies'];
            fetchFirstItems(20, serverController).then((res) => {
                if (res?.data) {
                    const list = applyRoleFilter(res.data);
                    setInitialCompanies(list);
                    setTotalCompaniesCount(res.total_count || list.length);
                }
            });
        }
    }, []);

    // helper to apply role based filtering on company list
    const applyRoleFilter = (arr) => {
        const systemRole = userInfo?.role?.systemRole;
        if (systemRole === 'sme_admin') {
            return arr.filter((c) => c.id === userInfo?.smeCompanyId);
        }
        return arr;
    };

    const handleFileSelect = (f) => {
        if (!f) return;
        setFile(f);
    };

    // Email validation function
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Validate parsed data for email format and duplicates
    const validateEmails = (parsedData) => {
        const errors = [];
        const emails = new Set();

        parsedData.forEach((item, index) => {
            const rowNumber = index + 1;

            // Check email format
            if (!isValidEmail(item.email)) {
                errors.push(
                    `Row ${rowNumber}: Invalid email format "${item.email}"`
                );
            }

            // Check for duplicates
            if (emails.has(item.email.toLowerCase())) {
                errors.push(
                    `Row ${rowNumber}: Duplicate email "${item.email}"`
                );
            } else {
                emails.add(item.email.toLowerCase());
            }
        });

        return errors;
    };

    // Parse uploaded file (CSV or Excel) and return array of { email, name }
    const parseFile = async () => {
        if (!file) return [];

        // Helper to normalise rows → { email, name }
        const normaliseRows = (rows) =>
            rows
                .map((row) => [
                    row[0]?.toString().trim(),
                    (row[1] || '').toString().trim()
                ])
                .filter(([email]) => !!email)
                .map(([email, name]) => ({ email, name }));

        const fileName = file.name.toLowerCase();

        // Handle CSV
        if (fileName.endsWith('.csv')) {
            const text = await file.text();
            const rows = text.split(/\r?\n/).map((line) => line.split(','));
            return normaliseRows(rows);
        }

        // Handle Excel (.xlsx/.xls)
        if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
            const arrayBuffer = await file.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const sheetData = XLSX.utils.sheet_to_json(worksheet, {
                header: 1
            });

            /* If first row looks like a header (contains "email"), skip it */
            const dataRows =
                sheetData.length &&
                typeof sheetData[0][0] === 'string' &&
                sheetData[0][0].toLowerCase().includes('email')
                    ? sheetData.slice(1)
                    : sheetData;

            return normaliseRows(dataRows);
        }

        // Unsupported format – return empty
        return [];
    };

    const handleReview = async () => {
        const parsed = await parseFile();

        // Validate emails before showing preview
        const validationErrors = validateEmails(parsed);
        if (validationErrors.length > 0) {
            setAlertText('Validation errors:\n' + validationErrors.join('\n'));
            setAlertSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setPreviewData(parsed);
        setStep(1);
    };

    const handleProceed = async () => {
        try {
            // Validate selectedCompany
            if (!selectedCompany || !selectedCompany.id) {
                throw new Error('No company selected');
            }

            // Use the already validated previewData instead of parsing again
            const employeesData = previewData.map((item) => ({
                email: item.email,
                name: item.name
            }));

            // Call the new API endpoint
            const serverController =
                context.getState().server['smeCompanyEmployees'];
            const response = await serverController.importEmployees(
                selectedCompany.id,
                employeesData
            );

            if (response.error !== 'OK') {
                throw new Error(response.error || 'Import failed');
            }

            // Call the parent callback if provided
            if (onUpload) onUpload(employeesData, selectedCompany?.id);

            // Show success message
            setAlertText('Employees imported successfully');
            setAlertSeverity('success');
            setSnackbarOpen(true);

            // reset local state
            resetState();
            onClose();
        } catch (error) {
            console.error('Import error:', error);
            setAlertText('Import failed: ' + error.message);
            setAlertSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const renderStep0 = () => (
        <>
            {/* SME Company choose */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {i18n.t('columns.campaignName')}
            </Typography>
            <SearchBox
                items={initialCompanies}
                totalItemsCount={totalCompaniesCount}
                serverSearch={true}
                searchFields={['name', 'email']}
                searchController={'sme-companies'}
                primaryKey="name"
                secondaryKey="email"
                primaryTitle={i18n.t('columns.campaignName')}
                secondaryTitle={i18n.t('columns.owner')}
                icon={<ProfileCircleBulkIcon />}
                addAssociationLabel={i18n.t('buttons.campaignNameAdd')}
                translatedSearchLabel={i18n.t('pages.smeCompanies.title')}
                handleChange={(value) => setSelectedCompany(value)}
                value={selectedCompany}
                multiple={false}
                context={context}
                translatedLabel={i18n.t('columns.campaignName')}
                saveFullData={true}
            />
            {/* File upload */}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                CSV/Excel Upload
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Paper
                    elevation={0}
                    sx={dragStyles}
                    component="label"
                    htmlFor="csv-upload-input"
                >
                    <input
                        id="csv-upload-input"
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        hidden
                        onChange={(e) => handleFileSelect(e.target.files[0])}
                    />
                    {file ? file.name : 'Drag and drop your document here.'}
                </Paper>
                <Button
                    variant="outlined"
                    component="label"
                    htmlFor="csv-upload-input"
                    sx={{ alignSelf: 'flex-start' }}
                >
                    {i18n.t('buttons.browse') || 'Browse files'}
                </Button>
            </Box>
        </>
    );

    const renderStep1 = () => (
        <>
            <Typography variant="h6" sx={{ mb: 2 }}>
                CSV/Excel Review
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>E-mail Address</TableCell>
                            <TableCell>Name</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {previewData.map((row, idx) => (
                            <TableRow key={idx}>
                                <TableCell>{row.email}</TableCell>
                                <TableCell>{row.name}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </>
    );

    return (
        <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {i18n.t('dialogs.batchFileUpload') || 'Batch File Upload'}
            </DialogTitle>
            <DialogContent dividers>
                {step === 0 ? renderStep0() : renderStep1()}
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={handleDialogClose}>
                    {i18n.t('buttons.cancel')}
                </Button>
                {step === 0 && (
                    <Button
                        variant="contained"
                        disabled={!file || !selectedCompany}
                        onClick={handleReview}
                    >
                        {i18n.t('buttons.review') || 'Review'}
                    </Button>
                )}
                {step === 1 && (
                    <Button variant="contained" onClick={handleProceed}>
                        {i18n.t('buttons.proceed') || 'Proceed'}
                    </Button>
                )}
            </DialogActions>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={alertSeverity}
                    sx={{ width: '100%' }}
                >
                    {alertText}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}

BatchUploadDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onUpload: PropTypes.func.isRequired,
    context: PropTypes.object,
    userInfo: PropTypes.object
};
